import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dumbbell, Utensils, Sparkles } from "lucide-react";

const MESSAGES = {
  diet: [
    "Sto costruendo la tua settimana su misura…",
    "Bilancio proteine, carboidrati e grassi…",
    "Scelgo pasti gustosi e vari per ogni giorno…",
    "Rispetto allergie, patologie e i tuoi gusti…",
    "Ci siamo quasi, ultimi ritocchi!",
  ],
  foods: [
    "Seleziono gli alimenti giusti per te…",
    "Penso ai tuoi gusti e preferenze…",
    "Bilancio le categorie alimentari…",
    "Ci siamo quasi!",
  ],
  supplements: [
    "Analizzo il tuo profilo…",
    "Cerco gli integratori più adatti…",
    "Controllo dosaggi e tempistiche…",
  ],
  blood: [
    "Leggo il tuo referto…",
    "Estraggo i valori dalle analisi…",
    "Interpreto i risultati…",
  ],
};

const FLOATERS = [Utensils, Dumbbell, Sparkles];

export default function AiLoader({ type = "diet", title }) {
  const { user } = useAuth();
  const sex = user?.profile?.sex === "donna" ? "woman" : "man";
  const messages = MESSAGES[type] || MESSAGES.diet;

  const [frame, setFrame] = useState(0); // 0 = eating, 1 = workout
  const [msg, setMsg] = useState(0);

  useEffect(() => {
    const f = setInterval(() => setFrame((v) => (v === 0 ? 1 : 0)), 2600);
    const m = setInterval(() => setMsg((v) => (v + 1) % messages.length), 2600);
    return () => { clearInterval(f); clearInterval(m); };
  }, [messages.length]);

  const imgs = useMemo(() => ([
    `/loader/${sex}_eating.jpg`,
    `/loader/${sex}_workout.jpg`,
  ]), [sex]);

  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6 select-none" data-testid="ai-loader">
      <div className="relative h-44 w-44 mb-7">
        {/* pulsing brand rings */}
        <span className="absolute inset-0 rounded-full bg-primary/25 ai-ring" />
        <span className="absolute inset-0 rounded-full bg-accent/20 ai-ring" style={{ animationDelay: "1.2s" }} />

        {/* character medallion (cross-fades between eating & workout) */}
        <div className="ai-bob relative h-44 w-44">
          <div className="absolute inset-0 rounded-full overflow-hidden ring-4 ring-white dark:ring-white/10 shadow-xl">
            {imgs.map((src, i) => (
              <img
                key={src}
                src={src}
                alt="DietFlix coach"
                className="absolute inset-0 h-full w-full object-cover scale-110 transition-opacity duration-700"
                style={{ opacity: frame === i ? 1 : 0 }}
                data-testid={`ai-loader-frame-${i}`}
              />
            ))}
          </div>
        </div>

        {/* floating icons */}
        {FLOATERS.map((Ic, i) => (
          <span
            key={i}
            className="absolute ai-float text-primary"
            style={{
              left: `${18 + i * 30}%`,
              bottom: "8%",
              animationDelay: `${i * 0.85}s`,
            }}
          >
            <Ic className="h-5 w-5" />
          </span>
        ))}
      </div>

      <h3 className="font-heading text-xl font-semibold" data-testid="ai-loader-title">
        {title || "DietFlix sta elaborando per te"}
      </h3>
      <p key={msg} className="ai-msg text-muted-foreground mt-2 min-h-[24px] text-sm" data-testid="ai-loader-message">
        {messages[msg]}
      </p>

      {/* indeterminate progress bar */}
      <div className="mt-6 h-2 w-56 max-w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-primary to-accent ai-shimmer" />
      </div>
      <p className="text-xs text-muted-foreground mt-3">Di solito bastano pochi secondi ⏱️</p>
    </div>
  );
}
